import { Link } from "react-router-dom";
import { Phone, ShieldCheck, HeartHandshake, MessagesSquare, Recycle } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SITE, IMAGES } from "../data/site";

const VALUES = [
  {
    icon: HeartHandshake,
    title: "Judgment-free, always",
    text: "However the property looks, whatever the circumstances — we've seen it before, and our only job is to help. No lectures, no raised eyebrows.",
  },
  {
    icon: ShieldCheck,
    title: "Clear, written quotes",
    text: "You get a written quote before work begins, so you can see the scope and cost up front.",
  },
  {
    icon: MessagesSquare,
    title: "Real communication",
    text: "Plain-language updates and a phone that gets answered — you'll always know where things stand.",
  },
  {
    icon: Recycle,
    title: "Donation-first thinking",
    text: "Useful belongings should keep being useful. We coordinate with local charities so good items find second homes instead of landfills.",
  },
];

export default function AboutPage() {
  usePageMeta(
    "About Us | MacWay Estate Services — DC, MD & VA",
    "MacWay Estate Services is a local company helping families and professionals across DC, Maryland, and Virginia with estate sales, cleanouts, downsizing, and judgment-free home help."
  );

  return (
    <main data-testid="about-page">
      <section className="bg-forest text-ivory py-20 md:py-28" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <p className="eyebrow-label">About MacWay</p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight" data-testid="about-headline">
            The people you want in the house, when the house is the hard part
          </h1>
          <p className="mt-6 text-lg text-ivory/80 leading-relaxed max-w-2xl">
            MacWay Estate Services is a local company serving {SITE.serviceArea}. We help families, executors,
            and professionals through the practical side of life's big transitions — estates, moves, and homes
            that have become more than one person can manage.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="eyebrow-label">How we work</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Warm people. Professional standards.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed">
              We started MacWay because these moments — settling an estate, moving a parent, facing a house that
              got away from you — deserve better than a rushed crew and a dumpster. They deserve patient people
              who treat a family's belongings like they matter, because they do.
            </p>
            <p className="mt-4 text-base sm:text-lg text-ink/70 leading-relaxed">
              Every project runs the same way: a walkthrough, a written quote, and careful
              work until the keys are ready to hand over.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-brass" data-testid="about-quote-cta">Request a Quote</Link>
              <a href={SITE.phoneHref} className="btn-outline-dark" data-testid="about-call-cta">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.team2} alt="A MacWay team member moving furniture with care" loading="lazy" className="w-full h-80 object-cover border border-linen" />
            <img src={IMAGES.team1} alt="The MacWay team loading items securely" loading="lazy" className="w-full h-80 object-cover border border-linen mt-8" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">What you can count on</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">Our promises, in plain terms</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white border border-linen p-8">
                <v.icon className="w-8 h-8 text-brass" />
                <h3 className="mt-5 text-2xl font-medium text-ink">{v.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="about-referral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-linen p-10 md:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="eyebrow-label">For referral professionals</p>
              <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">
                A partner your clients will thank you for
              </h2>
              <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">
                Real estate agents, probate and estate attorneys, senior move managers, and financial advisors
                trust MacWay with their clients' homes. We communicate clearly, document our work, protect your
                timeline — and make you look good for referring us.
              </p>
            </div>
            <ul className="space-y-4">
              {[
                "Prompt walkthroughs that keep listings and settlements on schedule",
                "Written quotes and clear accounting you can share with clients",
                "Respectful crews your clients will feel comfortable having in their home",
                "Direct line to the team — call " + SITE.phone + " anytime",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3 text-base text-ink/75">
                  <span className="mt-1.5 w-2 h-2 bg-brass shrink-0" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16 bg-sand" data-testid="about-recruiting">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="eyebrow-label">Work with us</p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">
            Interested in working with MacWay?
          </h2>
          <p className="mt-4 text-base text-ink/70 leading-relaxed">
            We maintain an on-call team for estate sales, cleanouts, moving, and short-term assignments.
          </p>
          <Link to="/employment-opportunities" className="btn-brass mt-7" data-testid="about-careers-link">
            View Employment Opportunities
          </Link>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
